<script setup lang="ts">
import AdjectiveInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/AdjectiveInflectionSchemeModal.vue";
import InflectionSchemeCellPlaceholder from "@/components/inflectionTrainer/InflectionSchemeCellPlaceholder.vue";
import { makeInflectionCell } from "@/helpers/dictsys-inflection-scheme/inflection-scheme.helper";
import type { DictsysInflectionSchemeGermanAdjectiveCase } from "@/types/article-formats/dictsys";

interface Affix {
  masculine: string;
  feminine: string;
  neutral: string;
  plural: string;
}

const makeInflectionSchemeCase = (
  prefixes: Affix,
  suffixes: Affix
): DictsysInflectionSchemeGermanAdjectiveCase => ({
  singular: {
    masculine: makeInflectionCell("", prefixes.masculine, suffixes.masculine),
    feminine: makeInflectionCell("", prefixes.feminine, suffixes.feminine),
    neutral: makeInflectionCell("", prefixes.neutral, suffixes.neutral)
  },
  plural: makeInflectionCell("", prefixes.plural, suffixes.plural)
});

const inflectionScheme = {
  positive: {
    "0": makeInflectionCell("", "", "")[0],
    weak: {
      nominative: makeInflectionSchemeCase(
        {
          masculine: "der",
          feminine: "die",
          neutral: "das",
          plural: "die"
        },
        {
          masculine: "e",
          feminine: "e",
          neutral: "e",
          plural: "en"
        }
      ),
      akkusative: makeInflectionSchemeCase(
        {
          masculine: "den",
          feminine: "die",
          neutral: "das",
          plural: "die"
        },
        {
          masculine: "en",
          feminine: "e",
          neutral: "e",
          plural: "en"
        }
      ),
      genitive: makeInflectionSchemeCase(
        {
          masculine: "des",
          feminine: "der",
          neutral: "des",
          plural: "der"
        },
        {
          masculine: "en",
          feminine: "en",
          neutral: "en",
          plural: "en"
        }
      ),
      dative: makeInflectionSchemeCase(
        {
          masculine: "dem",
          feminine: "der",
          neutral: "dem",
          plural: "den"
        },
        {
          masculine: "en",
          feminine: "en",
          neutral: "en",
          plural: "en"
        }
      )
    },
    mixed: {
      nominative: makeInflectionSchemeCase(
        {
          masculine: "ein",
          feminine: "eine",
          neutral: "ein",
          plural: "keine"
        },
        {
          masculine: "er",
          feminine: "e",
          neutral: "es",
          plural: "en"
        }
      ),
      akkusative: makeInflectionSchemeCase(
        {
          masculine: "einen",
          feminine: "eine",
          neutral: "ein",
          plural: "keine"
        },
        {
          masculine: "en",
          feminine: "e",
          neutral: "es",
          plural: "en"
        }
      ),
      genitive: makeInflectionSchemeCase(
        {
          masculine: "eines",
          feminine: "einer",
          neutral: "eines",
          plural: "keiner"
        },
        {
          masculine: "en",
          feminine: "en",
          neutral: "en",
          plural: "en"
        }
      ),
      dative: makeInflectionSchemeCase(
        {
          masculine: "einem",
          feminine: "einer",
          neutral: "einem",
          plural: "keinen"
        },
        {
          masculine: "en",
          feminine: "en",
          neutral: "en",
          plural: "en"
        }
      )
    },
    strong: {
      nominative: makeInflectionSchemeCase(
        { masculine: "", feminine: "", neutral: "", plural: "" },
        {
          masculine: "er",
          feminine: "e",
          neutral: "es",
          plural: "e"
        }
      ),
      akkusative: makeInflectionSchemeCase(
        { masculine: "", feminine: "", neutral: "", plural: "" },
        {
          masculine: "en",
          feminine: "e",
          neutral: "es",
          plural: "e"
        }
      ),
      genitive: makeInflectionSchemeCase(
        { masculine: "", feminine: "", neutral: "", plural: "" },
        {
          masculine: "en",
          feminine: "er",
          neutral: "en",
          plural: "er"
        }
      ),
      dative: makeInflectionSchemeCase(
        { masculine: "", feminine: "", neutral: "", plural: "" },
        {
          masculine: "em",
          feminine: "er",
          neutral: "em",
          plural: "en"
        }
      )
    }
  }
};
</script>

<template>
  <AdjectiveInflectionSchemeModal :inflection-scheme="inflectionScheme" hide-comparisons>
    <template #inflection-cell="{ inflections }">
      <InflectionSchemeCellPlaceholder v-if="inflections.length > 0" :inflection="inflections[0]" />
    </template>
  </AdjectiveInflectionSchemeModal>
</template>
